/**
 * Create a same-day partner check-in queue entry (4-digit code).
 * Uses franchise-flow token against Naturals salon-admin API.
 */

import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import {
  readFranchiseFlowAuth,
  getFranchiseOrganizationId,
  getFranchiseStylistId,
  hasFranchiseFlowAuth,
  franchiseAuthHeaders,
} from '../../shared/franchiseFlowAuth';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import {
  checkinQueueFetchInit,
  EMPTY_QUEUE,
} from '../../admin-shared/checkinQueueFetch';
import { buildHqSessionHeaders, hqSessionFetchInit } from '../../admin-shared/hqSessionAuth';
import { PORTAL_ID } from './api';

function hasPartnerSession() {
  return Boolean(localStorage.getItem('naturals_partner_userId'));
}

function adminBase() {
  return (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
}

function authHeaders(extra = {}) {
  return buildHqSessionHeaders(PORTAL_ID, extra);
}

function kioskFetchHeaders(extra = {}) {
  return franchiseAuthHeaders({ 'Content-Type': 'application/json', ...extra });
}

function stylistPayload(extra = {}) {
  const auth = readFranchiseFlowAuth();
  const stylistId =
    extra.stylistId ||
    extra.stylist_id ||
    getFranchiseStylistId() ||
    auth?.stylist_id ||
    auth?.login_account_id ||
    null;
  const stylistName =
    extra.stylistName ||
    extra.stylist_name ||
    auth?.full_name ||
    auth?.username ||
    null;
  return {
    stylist_id: stylistId || undefined,
    stylist_name: stylistName || undefined,
  };
}

/**
 * @param {{
 *   faceUserId?: string,
 *   customerName?: string,
 *   mobile?: string,
 *   selectedServices?: array,
 *   customerSnapshot?: object,
 *   organizationId?: string,
 *   source?: string,
 *   startService?: boolean,
 *   autoComplete?: boolean,
 *   referenceToken?: string,
 *   stylistId?: string,
 *   stylistName?: string,
 * }} payload
 */
export async function createPartnerCheckinQueue(payload = {}) {
  const base = adminBase();
  if (!base) {
    throw new Error('Salon admin API is not configured');
  }

  const orgId =
    payload.organizationId ||
    payload.organization_id ||
    getFranchiseOrganizationId() ||
    '';
  if (!orgId) {
    throw new Error('Organization is required for partner check-in');
  }

  const startService = Boolean(
    payload.startService ??
      payload.start_service ??
      payload.autoComplete ??
      payload.auto_complete,
  );

  const body = {
    organization_id: orgId,
    face_user_id: payload.faceUserId || payload.face_user_id || undefined,
    customer_name: payload.customerName || payload.customer_name || '',
    mobile: payload.mobile || '',
    selected_services: payload.selectedServices || payload.selected_services || [],
    customer_snapshot: payload.customerSnapshot || payload.customer_snapshot || {},
    source: payload.source || 'face_identity',
    start_service: startService,
    // Legacy alias for older backends
    auto_complete: startService,
    force_new: Boolean(payload.forceNew || payload.force_new) || undefined,
    reference_token: payload.referenceToken || payload.reference_token || undefined,
    assigned_staff: payload.assignedStaff || payload.assigned_staff || undefined,
    ...stylistPayload(payload),
  };

  const res = await fetchWithTimeout(`${base}/checkin-queue`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
    timeoutMs: 15000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || data?.message || 'Failed to create check-in');
  }
  return data.checkin || null;
}

export async function fetchCheckinQueue({ status = 'all', preferFranchise = false, fromDate, toDate } = {}) {
  const base = adminBase();
  const hasFranchise = preferFranchise && hasFranchiseFlowAuth();
  const hasSession = hasFranchise || hasPartnerSession();
  if (!base || !hasSession) {
    return { ...EMPTY_QUEUE };
  }

  const params = new URLSearchParams({ status: status || 'all' });
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);

  const res = await fetch(
    `${base}/checkin-queue?${params.toString()}`,
    checkinQueueFetchInit(PORTAL_ID, undefined, {
      headers: kioskFetchHeaders(),
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      return { ...EMPTY_QUEUE };
    }
    throw new Error(data?.error || 'Failed to load check-ins');
  }
  return data;
}

/** Partner live floor: mobile lookup (same core as stylist-checkin). */
export async function lookupPartnerCustomerByMobile(mobile) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) {
    throw new Error('Partner session required');
  }
  const res = await fetchWithTimeout(`${base}/partner/check-ins/lookup-mobile`, {
    ...hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile: String(mobile || '').trim() }),
    }),
    timeoutMs: 30000,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 404 || data?.status === 'not_found' || data?.code === 'NOT_FOUND') {
    const err = new Error(data?.message || 'No profile found for this mobile number');
    err.code = 'NOT_FOUND';
    err.payload = data;
    throw err;
  }
  if (res.status === 409 || data?.code === 'AMBIGUOUS') {
    const err = new Error(data?.message || 'Multiple profiles match this mobile number');
    err.code = 'AMBIGUOUS';
    err.candidates = Array.isArray(data?.candidates) ? data.candidates : [];
    err.payload = data;
    throw err;
  }
  if (!res.ok || !data?.success || !data?.user) {
    throw new Error(data?.message || data?.error || `Lookup failed (${res.status})`);
  }
  return data;
}

export async function fetchPartnerFloorGuestProfile(faceUserId) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) throw new Error('Partner session required');
  const id = String(faceUserId || '').trim();
  const res = await fetchWithTimeout(
    `${base}/partner/check-ins/guest-profile/${encodeURIComponent(id)}`,
    { ...hqSessionFetchInit(PORTAL_ID), timeoutMs: 30000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load guest profile');
  }
  return data;
}

export async function updatePartnerFloorGuestProfile(faceUserId, profile = {}) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) throw new Error('Partner session required');
  const id = String(faceUserId || '').trim();
  const res = await fetchWithTimeout(
    `${base}/partner/check-ins/guest-profile/${encodeURIComponent(id)}`,
    hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile }),
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to update guest profile');
  }
  return data;
}

export async function registerPartnerDeskGuest(profile = {}) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) throw new Error('Partner session required');
  const res = await fetchWithTimeout(`${base}/partner/check-ins/register-guest`, {
    ...hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profile || {}),
    }),
    timeoutMs: 60000,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 || data?.code === 'AMBIGUOUS') {
    const err = new Error(data?.message || 'Multiple profiles match this mobile number');
    err.code = 'AMBIGUOUS';
    err.candidates = Array.isArray(data?.candidates) ? data.candidates : [];
    throw err;
  }
  if (!res.ok || !data?.success) {
    throw new Error(data?.message || data?.error || 'Registration failed');
  }
  return data;
}

export async function fetchPartnerCheckinDeskEvents({ limit = 40, sinceMinId } = {}) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) {
    return { events: [] };
  }
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  if (sinceMinId != null && String(sinceMinId).trim() !== "") {
    params.set("sinceMinId", String(sinceMinId));
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(
    `${base}/partner/check-ins/events${qs}`,
    checkinQueueFetchInit(PORTAL_ID),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) return { events: [] };
    throw new Error(data?.error || "Failed to load desk events");
  }
  return data;
}

/**
 * Partner check-in analytics for the signed-in franchise (JWT organization_id).
 * @param {{ fromDate?: string, toDate?: string }} range
 */
export async function fetchCheckinAnalytics({ fromDate, toDate } = {}) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) {
    throw new Error('Not authenticated');
  }

  const params = new URLSearchParams();
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const res = await fetch(
    `${base}/checkin-queue/analytics${qs}`,
    hqSessionFetchInit(PORTAL_ID),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load check-in analytics');
  }
  return data;
}

export async function fetchPassportDeskGuestProfile(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/passport-desk-profile`,
    { ...hqSessionFetchInit(PORTAL_ID), timeoutMs: 45000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load guest profile');
  }
  return data;
}

export async function updatePassportDeskGuestProfile(checkinId, profile = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/passport-desk-profile`,
    hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile }),
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to update guest details');
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
  }
  return data.checkin;
}

export async function acknowledgePassportKioskGuest(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/passport-kiosk-ack`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }),
      timeoutMs: 60000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to confirm guest');
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('checkin-queue-updated'));
  }
  return data.checkin;
}

export async function completePassportRegistration(checkinId, profile = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/passport-register`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile }),
      }),
      // Face encode + register on salon API can take ~60–120s on CPU (matches nginx/backend).
      timeoutMs: 130000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to complete registration');
  }
  return data.checkin;
}

export async function fetchActiveVisit({
  faceUserId,
  visitReference,
  checkinId,
  includeClosed = false,
} = {}) {
  const base = adminBase();
  if (!base) return null;
  if (!hasFranchiseFlowAuth() && !hasPartnerSession()) return null;

  const params = new URLSearchParams();
  if (faceUserId) params.set('faceUserId', String(faceUserId));
  if (visitReference) params.set('visitReference', String(visitReference));
  if (checkinId != null && String(checkinId).trim() !== '') {
    params.set('checkinId', String(checkinId));
  }
  if (includeClosed) params.set('includeClosed', '1');
  if (![...params.keys()].length) return null;

  const init = hasPartnerSession()
    ? { ...hqSessionFetchInit(PORTAL_ID), method: 'GET', timeoutMs: 12000 }
    : { method: 'GET', headers: kioskFetchHeaders(), timeoutMs: 12000 };

  const res = await fetchWithTimeout(`${base}/checkin-queue/active-visit?${params}`, init);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) return null;
  return data.checkin || null;
}

export async function fetchTodayEngagementBookingsPartner(day) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) throw new Error('Partner session required');
  const qs = day ? `?day=${encodeURIComponent(String(day).slice(0, 10))}` : '';
  const res = await fetchWithTimeout(`${base}/partner/check-ins/today-bookings${qs}`, {
    ...hqSessionFetchInit(PORTAL_ID),
    timeoutMs: 25000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load bookings');
  }
  return Array.isArray(data.bookings) ? data.bookings : [];
}

export async function checkinFromEngagementBookingPartner(appointmentId, { faceUserId } = {}) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) throw new Error('Partner session required');
  const body = {
    appointment_id: appointmentId,
    appointmentId,
  };
  if (faceUserId) {
    body.face_user_id = faceUserId;
    body.faceUserId = faceUserId;
  }
  const res = await fetchWithTimeout(`${base}/partner/check-ins/checkin-from-booking`, {
    ...hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
    timeoutMs: 35000,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data?.code === 'AMBIGUOUS') {
    const err = new Error(data?.message || 'Multiple profiles match this number');
    err.code = 'AMBIGUOUS';
    err.candidates = Array.isArray(data?.candidates) ? data.candidates : [];
    err.booking = data?.booking;
    throw err;
  }
  if (res.status === 400 && data?.code === 'PHONE_REQUIRED') {
    const err = new Error(data?.error || 'Phone required');
    err.code = 'PHONE_REQUIRED';
    err.booking = data?.booking;
    throw err;
  }
  if (!res.ok || !data?.success || !data?.checkin?.id) {
    throw new Error(data?.error || data?.message || `Check-in failed (${res.status})`);
  }
  return data;
}

export async function patchCheckinVisitIntentPartner(checkinId, visitIntent) {
  const base = adminBase();
  if (!base || !hasPartnerSession()) throw new Error('Partner session required');
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/visit-intent`,
    hqSessionFetchInit(PORTAL_ID, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visit_intent: visitIntent }),
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to update visit intent');
  }
  return data.checkin;
}

export async function completeCheckinQueue(checkinId, { notes } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');

  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/complete`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || undefined }),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to complete check-in');
  }
  return data.checkin;
}

export async function startServiceCheckinQueue(checkinId, extras = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const auth = readFranchiseFlowAuth();
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');

  const body = {
    ...stylistPayload(extras),
    assigned_staff: extras.assignedStaff || extras.assigned_staff || undefined,
    selected_services: extras.selectedServices || extras.selected_services || undefined,
  };

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/start-service`,
    {
      method: 'POST',
      headers: kioskFetchHeaders(),
      body: JSON.stringify(body),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to start service');
  }
  return data.checkin;
}

export async function assignStaffToCheckin(checkinId, assignedStaff = []) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }
  const init = hasFranchiseFlowAuth()
    ? {
        method: 'POST',
        headers: kioskFetchHeaders(),
        body: JSON.stringify({ assigned_staff: assignedStaff }),
      }
    : hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_staff: assignedStaff }),
      });
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/assign-staff`,
    { ...init, timeoutMs: 15000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to assign staff');
  }
  return data.checkin;
}

export async function updateCheckinServices(checkinId, { selectedServices = [], assignedStaff } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }
  const body = {
    selected_services: selectedServices,
    assigned_staff: assignedStaff,
  };
  const init = hasFranchiseFlowAuth()
    ? {
        method: 'PUT',
        headers: kioskFetchHeaders(),
        body: JSON.stringify(body),
      }
    : hqSessionFetchInit(PORTAL_ID, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/services`,
    { ...init, timeoutMs: 15000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to update services');
  }
  return data.checkin;
}

export async function reassignServiceLineStaff(
  checkinId,
  { lineIds, toStaff, fromStaffId } = {},
) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }
  const body = {
    line_ids: Array.isArray(lineIds) ? lineIds : [],
    to_staff: toStaff,
    from_staff_id: fromStaffId || undefined,
  };
  const init = hasFranchiseFlowAuth()
    ? {
        method: 'POST',
        headers: kioskFetchHeaders(),
        body: JSON.stringify(body),
      }
    : hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/reassign-service-staff`,
    { ...init, timeoutMs: 15000 },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to reassign stylist');
  }
  return data.checkin;
}

export async function linkFaceToCheckin(checkinId, { faceUserId, customerSnapshot } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Partner session required');
  }
  if (!faceUserId) throw new Error('faceUserId is required');

  const init = hasPartnerSession()
    ? hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          face_user_id: faceUserId,
          customer_snapshot: customerSnapshot || undefined,
        }),
      })
    : {
        method: 'POST',
        headers: kioskFetchHeaders(),
        body: JSON.stringify({
          face_user_id: faceUserId,
          customer_snapshot: customerSnapshot || undefined,
        }),
      };

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/link-face`,
    {
      ...init,
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to link face profile');
  }
  return data.checkin;
}

export async function assignStylistToCheckin(checkinId, extras = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/assign-stylist`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...stylistPayload(extras),
          assigned_staff: extras.assignedStaff || extras.assigned_staff,
        }),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to assign stylist');
  }
  return data.checkin;
}

export async function cancelCheckinQueue(checkinId, { force = false } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession() && !hasFranchiseFlowAuth()) {
    throw new Error('Not authenticated');
  }

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/cancel`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: Boolean(force) }),
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to cancel check-in');
  }
  return data.checkin;
}

export async function reopenCheckinQueue(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) {
    throw new Error('Not authenticated');
  }

  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/reopen`,
    {
      ...hqSessionFetchInit(PORTAL_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to reopen check-in');
  }
  return data.checkin;
}

export async function lookupCheckinByCode(
  code,
  { complete = false, cancel = false, startService = false } = {},
) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  const auth = readFranchiseFlowAuth();
  if (!hasFranchiseFlowAuth()) throw new Error('Kiosk session required');

  const params = new URLSearchParams();
  if (cancel) params.set('cancel', '1');
  else if (complete) params.set('complete', '1');
  else if (startService) params.set('start_service', '1');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/by-code/${encodeURIComponent(String(code).trim())}${qs}`,
    {
      method: 'GET',
      headers: kioskFetchHeaders(),
      timeoutMs: 15000,
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Invalid check-in code');
  }
  return data.checkin;
}

/** Release an active queue entry when the guest abandons Welcome (Change code / leave). */
export async function releaseCheckinByCode(code) {
  return lookupCheckinByCode(code, { complete: false, cancel: true });
}

export async function fetchCheckinReportSessions(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/report-sessions`,
    hqSessionFetchInit(PORTAL_ID, { method: 'GET', timeoutMs: 15000 }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Could not load report sessions');
  }
  return data;
}

export async function fetchCheckinRecommendedProducts(checkinId) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/recommended-products`,
    hqSessionFetchInit(PORTAL_ID, { method: 'GET', timeoutMs: 15000 }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to load recommended products');
  }
  return data;
}

export async function setCheckinProductPreview(checkinId, active = true) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/product-preview`,
    hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: Boolean(active) }),
      timeoutMs: 12000,
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to update product preview');
  }
  return data;
}

export async function markCheckinAnalysisProductsReady(
  checkinId,
  { source = 'analysis', salonReference = '', productCount = null } = {},
) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');
  const body = {
    source,
    salon_reference: salonReference || undefined,
  };
  if (productCount != null && Number.isFinite(Number(productCount))) {
    body.product_count = Math.max(0, Number(productCount));
  }
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/analysis-products-ready`,
    hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      timeoutMs: 12000,
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to mark analysis products ready');
  }
  return data;
}

export async function saveCheckinProductCart(checkinId, items, { source = 'frontdesk' } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/product-cart`,
    hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: Array.isArray(items) ? items : [], source }),
      timeoutMs: 15000,
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to save product cart');
  }
  return data;
}

export async function placeCheckinProductOrder(checkinId, items, { source = 'frontdesk' } = {}) {
  const base = adminBase();
  if (!base) throw new Error('API unavailable');
  if (!hasPartnerSession()) throw new Error('Partner session required');
  const res = await fetchWithTimeout(
    `${base}/checkin-queue/${encodeURIComponent(checkinId)}/place-product-order`,
    hqSessionFetchInit(PORTAL_ID, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items == null ? undefined : Array.isArray(items) ? items : [],
        source,
      }),
      timeoutMs: 20000,
    }),
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Failed to place product order');
  }
  return data;
}
